import { useEffect, useMemo, useRef, useState } from "react";
import { api, uploadAndProcess } from "../lib/api";
import { PageHeader, Empty, Modal, CrudActions } from "./Crud";
import {
    Upload, FileText, Brain, Layers, MessageCircle, Search, Star, Plus,
    Sparkles, BookOpen, Clock, AlertTriangle, GraduationCap, Target,
    CheckCircle2, FileQuestion, Network, ListChecks, ChevronLeft,
} from "lucide-react";

const LEVELS = [
    { k: "beginner", l: "Beginner" },
    { k: "gcse", l: "GCSE / High School" },
    { k: "college", l: "College / A-Level" },
    { k: "university", l: "University" },
    { k: "advanced", l: "Advanced" },
];

export default function MissedLectureMode({ onBack }) {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const load = async () => {
        const { data } = await api.get("/missed_lectures");
        setItems(data);
    };
    useEffect(() => { load().catch(() => {}); }, []);

    const filtered = items.filter(m =>
        !q || (`${m.title} ${m.module} ${(m.tags || []).join(" ")}`).toLowerCase().includes(q.toLowerCase())
    );

    return (
        <div data-testid="missed-lecture-mode">
            <button onClick={onBack} className="btn-ghost text-xs inline-flex items-center gap-1 mb-3" data-testid="mlm-back">
                <ChevronLeft size={14} /> Back to Study Hub
            </button>
            <PageHeader
                title="Missed Lecture Mode"
                subtitle="catch up · reconstruct · revise"
                action={
                    <button onClick={() => setOpen(true)} className="btn-primary inline-flex items-center gap-2"
                            data-testid="new-missed-lecture-btn">
                        <Plus size={16} /> New catch-up
                    </button>
                }
            />

            <div className="card-warm p-4 mb-5 flex items-start gap-3" data-testid="mlm-intro">
                <Sparkles size={16} className="mt-0.5 text-[var(--brand-magenta)]" />
                <div className="text-sm text-[var(--text-ink)]/75">
                    Upload your slides, handouts, reading list, module guide, or notes. We&apos;ll <strong>reconstruct
                    what was likely covered</strong>, surface knowledge gaps, build a revision toolkit, and tutor
                    you through it — without writing your coursework.
                </div>
            </div>

            <div className="relative max-w-md mb-4">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-ink)]/40" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search catch-ups…"
                       data-testid="mlm-search"
                       className="w-full pl-9 pr-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
            </div>

            {filtered.length === 0 ? (
                <Empty icon={BookOpen} title="No catch-ups yet"
                       hint="Upload missed-lecture materials (slides, PDF, DOCX) to reconstruct what was covered." />
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map(m => <MissedCard key={m.id} item={m} onOpen={() => setSelected(m)} />)}
                </div>
            )}

            <div className="mt-8 card-warm p-4 text-xs text-[var(--text-ink)]/65 flex items-start gap-2" data-testid="mlm-integrity">
                <AlertTriangle size={14} className="mt-0.5 text-[var(--brand-orange)]" />
                <span>This mode is for <strong>learning, revision, and comprehension</strong>. It will not draft
                    coursework, dissertations or essays intended for submission, and is designed not to circumvent
                    academic integrity policies.</span>
            </div>

            {open && <NewMissedLectureModal onClose={() => setOpen(false)}
                                            onCreated={async (m) => { await load(); setOpen(false); setSelected(m); }} />}
            {selected && <MissedLectureDetail item={selected}
                                              onClose={() => setSelected(null)}
                                              onUpdate={async (patched) => { await load(); setSelected(patched); }}
                                              onDeleted={async () => { await load(); setSelected(null); }} />}
        </div>
    );
}

function MissedCard({ item, onOpen }) {
    const sectionsTotal = (item.reconstruction?.topics || []).length;
    const sectionsDone = (item.progress?.sections_completed || []).length;
    const pct = sectionsTotal ? Math.round((sectionsDone / sectionsTotal) * 100) : 0;
    const hasRec = !!item.reconstruction;
    const hasRev = !!item.revision;
    return (
        <button onClick={onOpen} data-testid={`missed-card-${item.id}`}
                className="card-warm p-4 text-left hover:-translate-y-0.5 transition">
            <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                    {item.module && <div className="font-script text-[var(--brand-orange)] text-sm">{item.module}</div>}
                    <div className="font-serif text-lg truncate">{item.title || "Untitled catch-up"}</div>
                </div>
                {item.favorite && <Star size={14} className="text-[var(--brand-orange)] shrink-0" fill="currentColor" />}
            </div>
            <div className="text-xs text-[var(--text-ink)]/55 mt-2 flex items-center gap-3 flex-wrap">
                <span className="flex items-center gap-1"><FileText size={12} /> {(item.sources || []).length} sources</span>
                {hasRec && <span className="flex items-center gap-1"><Brain size={12} /> reconstructed</span>}
                {hasRev && <span className="flex items-center gap-1"><Layers size={12} /> revision</span>}
            </div>
            {sectionsTotal > 0 && (
                <div className="mt-3">
                    <div className="flex justify-between text-[10px] text-[var(--text-ink)]/55 mb-1">
                        <span>Progress</span><span>{pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-[rgba(26,19,24,0.08)] overflow-hidden">
                        <div className="h-full brand-gradient" style={{ width: `${pct}%` }} />
                    </div>
                </div>
            )}
        </button>
    );
}

function NewMissedLectureModal({ onClose, onCreated }) {
    const [title, setTitle] = useState("");
    const [module, setModule] = useState("");
    const [tags, setTags] = useState("");
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState("");
    const inputRef = useRef(null);

    const addFiles = (list) => {
        const next = [...files];
        for (const f of list) {
            if (!next.find(x => x.name === f.name && x.size === f.size)) next.push(f);
        }
        setFiles(next.slice(0, 20));
    };
    const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

    const submit = async () => {
        if (files.length === 0) { alert("Add at least one file."); return; }
        setBusy(true);
        try {
            // Stage 1+2: async upload → background extraction. The 30 s
            // frontend timeout that previously killed large PPTX uploads
            // no longer applies — the server persists to storage and
            // returns a job_id in < 3 s, then we poll to completion.
            setProgress("Uploading materials…");
            const ex = await uploadAndProcess({
                files,
                kind: "lecture_extract",
                onStatus: (s, info) => {
                    if (s === "uploading") {
                        const pct = typeof info?.percent === "number" ? info.percent : 0;
                        setProgress(`Uploading materials… ${pct}%`);
                    } else if (s === "processing") {
                        setProgress("Processing document…");
                    } else if (s === "complete") {
                        setProgress("Extraction complete. Reconstructing…");
                    }
                },
            });

            setProgress("Reconstructing the missed lecture with AI…");
            const { data: rec } = await api.post("/ai/missed-lecture-reconstruct", {
                combined_text: ex.combined_text, title, module,
            });

            setProgress("Saving catch-up…");
            const tagArr = tags.split(",").map(t => t.trim()).filter(Boolean);
            const payload = {
                title: title || "Untitled catch-up",
                module, tags: tagArr,
                sources: ex.sources, combined_text: ex.combined_text,
                reconstruction: rec,
                progress: { sections_completed: [], quizzes_passed: 0, confidence_scores: {}, materials_uploaded: ex.sources.length },
            };
            const { data: saved } = await api.post("/missed_lectures", payload);
            onCreated?.(saved);
        } catch (e) {
            alert("Failed: " + (e.friendly || e.response?.data?.detail || e.message));
        } finally { setBusy(false); setProgress(""); }
    };

    return (
        <Modal open onClose={onClose} title="New missed-lecture catch-up" size="lg">
            <div className="grid gap-3" data-testid="new-missed-form">
                <input placeholder="Title (e.g. Lecture 6 – Macroeconomic Policy)" value={title}
                       onChange={(e) => setTitle(e.target.value)} data-testid="ml-title"
                       className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                <div className="grid grid-cols-2 gap-2">
                    <input placeholder="Module (e.g. ECON202)" value={module}
                           onChange={(e) => setModule(e.target.value)} data-testid="ml-module"
                           className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                    <input placeholder="Tags (comma-separated)" value={tags}
                           onChange={(e) => setTags(e.target.value)} data-testid="ml-tags"
                           className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                </div>

                <div onDragOver={(e) => e.preventDefault()}
                     onDrop={(e) => { e.preventDefault(); addFiles(Array.from(e.dataTransfer.files || [])); }}
                     onClick={() => inputRef.current?.click()}
                     data-testid="ml-dropzone"
                     className="card-warm p-5 text-sm cursor-pointer hover:bg-[rgba(255,107,53,0.05)] transition border-2 border-dashed">
                    <div className="flex items-center gap-3">
                        <Upload size={18} className="text-[var(--brand-purple)]" />
                        <div>
                            <div className="font-medium">Drop or choose materials</div>
                            <div className="text-xs text-[var(--text-ink)]/60">Slides, PDFs, DOCX, notes, handouts, reading. PPTX, PDF, DOCX, TXT, MD · up to 20 files / 40 MB.</div>
                        </div>
                    </div>
                    <input ref={inputRef} type="file" multiple
                           accept=".pdf,.docx,.pptx,.txt,.md,application/pdf"
                           onChange={(e) => addFiles(Array.from(e.target.files || []))}
                           data-testid="ml-file-input" className="hidden" />
                </div>

                {files.length > 0 && (
                    <div className="grid gap-1.5 text-xs" data-testid="ml-file-list">
                        {files.map((f, i) => (
                            <div key={i} className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-[rgba(26,19,24,0.04)]">
                                <span className="truncate"><FileText size={12} className="inline mr-1" />{f.name}</span>
                                <button onClick={() => removeFile(i)} className="text-[var(--brand-magenta)] hover:underline">remove</button>
                            </div>
                        ))}
                    </div>
                )}

                {progress && <div className="text-xs text-[var(--text-ink)]/70" data-testid="ml-progress">{progress}</div>}

                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="btn-ghost">Cancel</button>
                    <button onClick={submit} disabled={busy} data-testid="ml-submit-btn" className="btn-primary">
                        {busy ? "Working…" : "Reconstruct & save"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

function MissedLectureDetail({ item: initial, onClose, onUpdate, onDeleted }) {
    const [item, setItem] = useState(initial);
    const [tab, setTab] = useState("summary");
    const [busy, setBusy] = useState(false);

    const save = async (patch) => {
        const { data } = await api.put(`/missed_lectures/${item.id}`, patch);
        setItem(data); onUpdate?.(data);
    };
    const remove = async () => {
        if (!window.confirm(`Delete catch-up "${item.title}"?`)) return;
        await api.delete(`/missed_lectures/${item.id}`); onDeleted?.();
    };
    const regenRevision = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/ai/missed-lecture-revision", {
                combined_text: item.combined_text, title: item.title, module: item.module,
            });
            await save({ revision: data });
        } finally { setBusy(false); }
    };
    const regenReconstruction = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/ai/missed-lecture-reconstruct", {
                combined_text: item.combined_text, title: item.title, module: item.module,
            });
            await save({ reconstruction: data });
        } finally { setBusy(false); }
    };

    const tabs = [
        { k: "summary", l: "Summary", I: BookOpen },
        { k: "topics", l: "Topics", I: Brain },
        { k: "keys", l: "Key Points", I: Target },
        { k: "gaps", l: "Knowledge Gaps", I: AlertTriangle },
        { k: "revision", l: "Revision Toolkit", I: Layers },
        { k: "explain", l: "Explain Like I'm New", I: GraduationCap },
        { k: "tutor", l: "AI Tutor", I: MessageCircle },
        { k: "progress", l: "Progress", I: ListChecks },
    ];

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-end sm:place-items-center p-0 sm:p-6"
             data-testid="missed-detail" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()}
                 className="card-warm w-full sm:max-w-4xl max-h-[92vh] overflow-auto scrollbar-thin">
                <div className="sticky top-0 bg-[var(--bg-cream)] border-b border-[rgba(26,19,24,0.08)] px-5 py-3 flex items-center gap-3 z-10">
                    {item.module && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--brand-orange)]/15 text-[var(--brand-orange)]">
                            {item.module}
                        </span>
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="font-serif text-xl truncate">{item.title}</div>
                        <div className="text-[11px] text-[var(--text-ink)]/55">
                            {(item.sources || []).length} sources · {Math.round((item.combined_text || "").length / 1000)}k chars
                        </div>
                    </div>
                    <CrudActions idBase={`ml-${item.id}`}
                                 isFav={item.favorite}
                                 onFavorite={() => save({ favorite: !item.favorite })}
                                 onDelete={remove} />
                    <button onClick={onClose} className="btn-ghost text-sm">Close</button>
                </div>

                <div className="px-5 py-4">
                    <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-thin">
                        {tabs.map(t => (
                            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`ml-tab-${t.k}`}
                                    className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 whitespace-nowrap ${tab === t.k ? "brand-gradient text-white" : "btn-ghost"}`}>
                                <t.I size={12} /> {t.l}
                            </button>
                        ))}
                    </div>

                    {tab === "summary" && <SummaryTab item={item} onRegen={regenReconstruction} busy={busy} />}
                    {tab === "topics" && <TopicsTab item={item} save={save} />}
                    {tab === "keys" && <KeysTab item={item} />}
                    {tab === "gaps" && <GapsTab item={item} />}
                    {tab === "revision" && <RevisionTab item={item} onRegen={regenRevision} busy={busy} save={save} />}
                    {tab === "explain" && <ExplainTab item={item} />}
                    {tab === "tutor" && <TutorTab item={item} />}
                    {tab === "progress" && <ProgressTab item={item} save={save} />}
                </div>
            </div>
        </div>
    );
}

function SummaryTab({ item, onRegen, busy }) {
    const rec = item.reconstruction || {};
    const st = rec.study_time || {};
    return (
        <div data-testid="ml-summary" className="space-y-4">
            <div className="card-warm p-4">
                <div className="font-serif text-lg mb-1">Virtual lecture summary</div>
                <div className="text-sm text-[var(--text-ink)]/80">{rec.summary || "—"}</div>
            </div>
            {(rec.learning_objectives || []).length > 0 && (
                <div className="card-warm p-4">
                    <div className="font-serif text-base mb-2">Learning objectives</div>
                    <ul className="text-sm list-disc pl-5 space-y-1">
                        {rec.learning_objectives.map((o, i) => <li key={i}>{o}</li>)}
                    </ul>
                </div>
            )}
            <div className="grid sm:grid-cols-3 gap-3">
                <StatBox label="Total study" value={`${Math.round((st.total_minutes || 0) / 60 * 10) / 10}h`} colour="var(--brand-purple)" icon={Clock} />
                <StatBox label="Reading" value={`${Math.round((st.reading_minutes || 0))}m`} colour="var(--brand-magenta)" icon={BookOpen} />
                <StatBox label="Revision" value={`${Math.round((st.revision_minutes || 0))}m`} colour="var(--brand-orange)" icon={Layers} />
            </div>
            {(st.schedule || []).length > 0 && (
                <div className="card-warm p-4">
                    <div className="font-serif text-base mb-2">Recommended schedule</div>
                    <ul className="text-sm space-y-1.5">
                        {st.schedule.map((s, i) => (
                            <li key={i} className="flex items-baseline gap-3">
                                <span className="font-mono text-xs text-[var(--brand-purple)] shrink-0 w-14">{s.day}</span>
                                <span className="flex-1">{s.focus}</span>
                                <span className="text-xs text-[var(--text-ink)]/55">{s.minutes}m</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {(rec.recommended_reading || []).length > 0 && (
                <div className="card-warm p-4">
                    <div className="font-serif text-base mb-2">Recommended reading</div>
                    <ul className="text-sm list-disc pl-5 space-y-1">
                        {rec.recommended_reading.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                </div>
            )}
            <button onClick={onRegen} disabled={busy} className="btn-ghost text-xs" data-testid="ml-regen-rec">
                {busy ? "Regenerating…" : "Regenerate reconstruction"}
            </button>
        </div>
    );
}

function StatBox({ label, value, colour, icon: Icon }) {
    return (
        <div className="card-warm p-3">
            <Icon size={14} style={{ color: colour }} className="mb-1" />
            <div className="text-[10px] uppercase tracking-wide text-[var(--text-ink)]/55">{label}</div>
            <div className="font-serif text-2xl" style={{ color: colour }}>{value}</div>
        </div>
    );
}

function TopicsTab({ item, save }) {
    const topics = item.reconstruction?.topics || [];
    const done = new Set(item.progress?.sections_completed || []);
    const toggle = (i) => {
        const next = new Set(done);
        if (next.has(i)) next.delete(i); else next.add(i);
        save({ progress: { ...(item.progress || {}), sections_completed: Array.from(next) } });
    };
    if (!topics.length) return <div className="text-sm text-[var(--text-ink)]/60">No topics yet.</div>;
    return (
        <div className="space-y-3" data-testid="ml-topics">
            {topics.map((t, i) => (
                <div key={i} className="card-warm p-4">
                    <div className="flex items-start gap-3">
                        <button onClick={() => toggle(i)} data-testid={`ml-topic-toggle-${i}`}
                                className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${done.has(i) ? "brand-gradient border-transparent text-white" : "border-[rgba(26,19,24,0.25)]"}`}>
                            {done.has(i) && <CheckCircle2 size={12} />}
                        </button>
                        <div className="flex-1 min-w-0">
                            <div className="font-serif text-lg">{t.heading}</div>
                            <div className="text-sm mt-1 text-[var(--text-ink)]/80">{t.plain_english}</div>
                            {(t.key_points || []).length > 0 && (
                                <ul className="text-sm list-disc pl-5 mt-2 space-y-0.5">
                                    {t.key_points.map((k, j) => <li key={j}>{k}</li>)}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function KeysTab({ item }) {
    const rec = item.reconstruction || {};
    const blocks = [
        { l: "Key concepts", arr: (rec.key_concepts || []).map(c => `${c.term} — ${c.definition}`) },
        { l: "Terminology", arr: (rec.terminology || []).map(c => `${c.term} — ${c.definition}`) },
        { l: "Formulas & dates", arr: rec.formulas_dates || [] },
        { l: "Case studies", arr: rec.case_studies || [] },
        { l: "Research findings", arr: rec.research_findings || [] },
        { l: "Frequently assessed", arr: rec.frequently_assessed || [] },
    ];
    return (
        <div className="space-y-3" data-testid="ml-keys">
            {blocks.map((b, i) => b.arr.length > 0 && (
                <div key={i} className="card-warm p-4">
                    <div className="font-serif text-base mb-2">{b.l}</div>
                    <ul className="text-sm list-disc pl-5 space-y-1">{b.arr.map((x, j) => <li key={j}>{x}</li>)}</ul>
                </div>
            ))}
            {blocks.every(b => b.arr.length === 0) && <div className="text-sm text-[var(--text-ink)]/60">No key points yet.</div>}
        </div>
    );
}

function GapsTab({ item }) {
    const gaps = item.reconstruction?.knowledge_gaps || [];
    if (!gaps.length) return <div className="text-sm text-[var(--text-ink)]/60">No gaps detected.</div>;
    return (
        <div className="space-y-3" data-testid="ml-gaps">
            {gaps.map((g, i) => (
                <div key={i} className="card-warm p-4 border-l-4" style={{ borderLeftColor: "var(--brand-magenta)" }}>
                    <div className="font-serif text-base">{g.topic}</div>
                    <div className="text-xs text-[var(--text-ink)]/55 mt-1">Why it&apos;s tricky</div>
                    <div className="text-sm">{g.why_difficult}</div>
                    <div className="text-xs text-[var(--text-ink)]/55 mt-2">What to do</div>
                    <div className="text-sm">{g.what_to_do}</div>
                </div>
            ))}
        </div>
    );
}

function RevisionTab({ item, onRegen, busy, save }) {
    const rev = item.revision;
    if (!rev) {
        return (
            <div data-testid="ml-revision">
                <button onClick={onRegen} disabled={busy} className="btn-primary inline-flex items-center gap-2"
                        data-testid="ml-gen-revision">
                    <Layers size={14} /> {busy ? "Generating…" : "Generate revision toolkit"}
                </button>
            </div>
        );
    }
    const passQuiz = () => save({
        progress: { ...(item.progress || {}), quizzes_passed: (item.progress?.quizzes_passed || 0) + 1 },
    });
    return (
        <div className="space-y-5" data-testid="ml-revision">
            {(rev.flashcards || []).length > 0 && (
                <div>
                    <div className="font-serif text-lg mb-2">Flashcards</div>
                    <div className="grid sm:grid-cols-2 gap-2">
                        {rev.flashcards.map((c, i) => <Flashcard key={i} card={c} />)}
                    </div>
                </div>
            )}
            {(rev.mcq || []).length > 0 && (
                <div>
                    <div className="font-serif text-lg mb-2">Multiple choice</div>
                    <div className="space-y-2">{rev.mcq.map((m, i) => <MCQ key={i} mcq={m} onAllCorrect={passQuiz} />)}</div>
                </div>
            )}
            {(rev.short_answer || []).length > 0 && (
                <div>
                    <div className="font-serif text-lg mb-2">Short answer</div>
                    <ul className="space-y-2">
                        {rev.short_answer.map((s, i) => (
                            <li key={i} className="card-warm p-3 text-sm">
                                <div className="font-medium">{s.q}</div>
                                <details className="mt-1">
                                    <summary className="text-xs text-[var(--brand-purple)] cursor-pointer">Reveal model answer</summary>
                                    <div className="text-[var(--text-ink)]/75 mt-1">{s.model_answer}</div>
                                </details>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {(rev.practice_exam_questions || []).length > 0 && (
                <div className="card-warm p-4 border-l-4" style={{ borderLeftColor: "var(--brand-orange)" }}>
                    <div className="font-serif text-base mb-2 inline-flex items-center gap-2">
                        <FileQuestion size={14} /> Practice exam questions
                    </div>
                    <ul className="text-sm list-disc pl-5 space-y-1">
                        {rev.practice_exam_questions.map((q, i) => <li key={i}>{q}</li>)}
                    </ul>
                </div>
            )}
            {rev.mind_map && (rev.mind_map.branches || []).length > 0 && (
                <div className="card-warm p-4">
                    <div className="font-serif text-base mb-2 inline-flex items-center gap-2">
                        <Network size={14} /> Mind map · {rev.mind_map.root}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                        {rev.mind_map.branches.map((b, i) => (
                            <div key={i} className="bg-[rgba(123,45,142,0.05)] rounded-lg p-3">
                                <div className="font-medium text-sm">{b.label}</div>
                                <ul className="text-xs list-disc pl-4 mt-1 space-y-0.5">
                                    {(b.children || []).map((c, j) => <li key={j}>{c}</li>)}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {(rev.topic_checklist || []).length > 0 && (
                <div className="card-warm p-4">
                    <div className="font-serif text-base mb-2">Topic checklist</div>
                    <ul className="text-sm space-y-1">
                        {rev.topic_checklist.map((t, i) => <li key={i} className="flex items-center gap-2">
                            <CheckCircle2 size={12} className="text-[var(--status-green)]" /> {t}
                        </li>)}
                    </ul>
                </div>
            )}
            {rev.summary && (
                <div className="card-warm p-4 border-l-4" style={{ borderLeftColor: "var(--brand-purple)" }}>
                    <div className="font-serif text-base mb-1">Revision summary</div>
                    <div className="text-sm">{rev.summary}</div>
                </div>
            )}
            <button onClick={onRegen} disabled={busy} className="btn-ghost text-xs">Regenerate</button>
        </div>
    );
}

function Flashcard({ card }) {
    const [flipped, setFlipped] = useState(false);
    return (
        <button onClick={() => setFlipped(f => !f)} data-testid="ml-flashcard"
                className="card-warm p-4 text-left min-h-[100px] hover:-translate-y-0.5 transition">
            <div className="text-[10px] uppercase tracking-wide" style={{ color: flipped ? "var(--brand-orange)" : "var(--brand-purple)" }}>
                {flipped ? "Answer" : "Question"}
            </div>
            <div className="text-sm mt-1">{flipped ? card.back : card.front}</div>
        </button>
    );
}

function MCQ({ mcq, onAllCorrect }) {
    const [picked, setPicked] = useState(null);
    const correct = mcq.answer_index;
    useEffect(() => { if (picked === correct && onAllCorrect) onAllCorrect(); }, [picked, correct, onAllCorrect]);
    return (
        <div className="card-warm p-3 text-sm">
            <div className="font-medium mb-2">{mcq.q}</div>
            <div className="grid sm:grid-cols-2 gap-1.5">
                {(mcq.options || []).map((opt, i) => {
                    const isPicked = picked === i;
                    const showCorrect = picked !== null && i === correct;
                    const showWrong = isPicked && i !== correct;
                    return (
                        <button key={i} onClick={() => setPicked(i)}
                                className={`text-left px-3 py-1.5 rounded-lg border transition ${
                                    showCorrect ? "border-[var(--status-green)] bg-[var(--status-green)]/10" :
                                    showWrong ? "border-[var(--status-red)] bg-[var(--status-red)]/10" :
                                    "border-[rgba(26,19,24,0.12)]"
                                }`}>
                            {String.fromCharCode(65 + i)}. {opt}
                        </button>
                    );
                })}
            </div>
            {picked !== null && mcq.rationale && (
                <div className="text-xs text-[var(--text-ink)]/70 mt-2 italic">{mcq.rationale}</div>
            )}
        </div>
    );
}

function ExplainTab({ item }) {
    const [topic, setTopic] = useState("");
    const [level, setLevel] = useState("university");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const explain = async () => {
        if (!topic.trim()) return;
        setBusy(true); setResult(null);
        try {
            const { data } = await api.post("/ai/missed-lecture-explain", {
                topic, level, context: item.combined_text,
            });
            setResult(data);
        } catch (e) {
            setResult({ explanation: "Failed: " + (e.response?.data?.detail || e.message) });
        } finally { setBusy(false); }
    };
    return (
        <div className="space-y-3" data-testid="ml-explain">
            <div className="text-xs text-[var(--text-ink)]/60">Pick a level, type any topic from your materials.</div>
            <div className="flex flex-wrap gap-1.5">
                {LEVELS.map(l => (
                    <button key={l.k} onClick={() => setLevel(l.k)} data-testid={`ml-level-${l.k}`}
                            className={`text-xs px-3 py-1.5 rounded-full ${level === l.k ? "brand-gradient text-white" : "btn-ghost"}`}>
                        {l.l}
                    </button>
                ))}
            </div>
            <div className="flex gap-2">
                <input value={topic} onChange={(e) => setTopic(e.target.value)}
                       placeholder="e.g. The Keynesian multiplier" data-testid="ml-explain-topic"
                       onKeyDown={(e) => e.key === "Enter" && explain()}
                       className="flex-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
                <button onClick={explain} disabled={busy} data-testid="ml-explain-btn" className="btn-primary text-sm">
                    {busy ? "…" : "Explain"}
                </button>
            </div>
            {result && (
                <div className="card-warm p-4 text-sm whitespace-pre-wrap" data-testid="ml-explain-result">
                    {result.explanation}
                </div>
            )}
        </div>
    );
}

function TutorTab({ item }) {
    const [log, setLog] = useState([]);
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState(false);
    const QUICK = [
        "Explain this lecture in 3 minutes.",
        "What should I revise first?",
        "Give me a worked example.",
        "Compare the main theories.",
        "Create a 5-question quiz.",
    ];
    const ask = async (preset) => {
        const question = (preset ?? q).trim();
        if (!question) return;
        setQ(""); setBusy(true);
        const next = [...log, { role: "user", text: question }];
        setLog(next);
        try {
            const { data } = await api.post("/ai/missed-lecture-ask", {
                context: item.combined_text, question, history: next,
            });
            setLog(l => [...l, { role: "ai", text: data.answer }]);
        } catch (e) {
            setLog(l => [...l, { role: "ai", text: "Sorry — " + (e.response?.data?.detail || e.message) }]);
        } finally { setBusy(false); }
    };
    return (
        <div data-testid="ml-tutor">
            <div className="text-xs text-[var(--text-ink)]/60 mb-2">
                AI tutor — answers come from your uploaded materials only. Won&apos;t write coursework for submission.
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
                {QUICK.map((qq, i) => (
                    <button key={i} onClick={() => ask(qq)} disabled={busy}
                            data-testid={`ml-quick-${i}`}
                            className="text-xs px-3 py-1.5 rounded-full btn-ghost">{qq}</button>
                ))}
            </div>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto scrollbar-thin mb-3">
                {log.map((m, i) => (
                    <div key={i} className={`card-warm p-3 text-sm ${m.role === "user" ? "bg-[rgba(123,45,142,0.05)]" : ""}`}>
                        <div className="text-[10px] uppercase tracking-wide text-[var(--text-ink)]/55 mb-1">
                            {m.role === "user" ? "You" : "AI tutor"}
                        </div>
                        <div className="whitespace-pre-wrap">{m.text}</div>
                    </div>
                ))}
                {busy && <div className="text-xs text-[var(--text-ink)]/55">Thinking…</div>}
            </div>
            <div className="flex gap-2">
                <input value={q} onChange={(e) => setQ(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && ask()}
                       placeholder="Ask anything about this lecture…" data-testid="ml-tutor-input"
                       className="flex-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm" />
                <button onClick={() => ask()} disabled={busy} data-testid="ml-tutor-send" className="btn-primary text-sm">
                    Ask
                </button>
            </div>
        </div>
    );
}

function ProgressTab({ item, save }) {
    const topics = item.reconstruction?.topics || [];
    const progress = item.progress || { sections_completed: [], quizzes_passed: 0, confidence_scores: {} };
    const done = (progress.sections_completed || []).length;
    const sectionsPct = topics.length ? Math.round((done / topics.length) * 100) : 0;
    const confidences = Object.values(progress.confidence_scores || {});
    const avgConf = confidences.length ? Math.round((confidences.reduce((a, b) => a + b, 0) / confidences.length) * 10) / 10 : 0;
    const readiness = useMemo(() => {
        const a = sectionsPct;
        const b = Math.min(100, (progress.quizzes_passed || 0) * 25);
        const c = (avgConf / 5) * 100;
        return Math.round((a * 0.4 + b * 0.3 + c * 0.3));
    }, [sectionsPct, progress.quizzes_passed, avgConf]);

    const setConfidence = (i, v) => save({
        progress: { ...progress, confidence_scores: { ...(progress.confidence_scores || {}), [i]: v } },
    });

    return (
        <div className="space-y-4" data-testid="ml-progress">
            <div className="grid sm:grid-cols-4 gap-3">
                <StatBox label="Materials" value={(item.sources || []).length} colour="var(--brand-purple)" icon={FileText} />
                <StatBox label="Sections done" value={`${done}/${topics.length || 0}`} colour="var(--brand-magenta)" icon={CheckCircle2} />
                <StatBox label="Quizzes passed" value={progress.quizzes_passed || 0} colour="var(--brand-orange)" icon={Target} />
                <StatBox label="Avg confidence" value={`${avgConf || 0}/5`} colour="var(--status-green)" icon={GraduationCap} />
            </div>

            <div className="card-warm p-4">
                <div className="flex justify-between items-baseline mb-2">
                    <div className="font-serif text-base">Revision readiness</div>
                    <div className="text-2xl font-serif" style={{ color: "var(--brand-orange)" }}>{readiness}%</div>
                </div>
                <div className="h-2 rounded-full bg-[rgba(26,19,24,0.08)] overflow-hidden">
                    <div className="h-full brand-gradient" style={{ width: `${readiness}%` }} />
                </div>
                <div className="text-xs text-[var(--text-ink)]/55 mt-2">
                    Combines section completion, quiz passes, and your confidence ratings.
                </div>
            </div>

            {topics.length > 0 && (
                <div className="card-warm p-4">
                    <div className="font-serif text-base mb-3">Confidence per topic</div>
                    <div className="space-y-2">
                        {topics.map((t, i) => (
                            <div key={i} className="flex items-center gap-3">
                                <div className="flex-1 min-w-0 truncate text-sm">{t.heading}</div>
                                <div className="flex gap-1">
                                    {[1, 2, 3, 4, 5].map(v => (
                                        <button key={v} onClick={() => setConfidence(i, v)}
                                                data-testid={`ml-conf-${i}-${v}`}
                                                className={`w-6 h-6 rounded-full text-xs ${
                                                    (progress.confidence_scores?.[i] || 0) >= v
                                                        ? "brand-gradient text-white"
                                                        : "bg-[rgba(26,19,24,0.06)] text-[var(--text-ink)]/55"
                                                }`}>
                                            {v}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
